import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ColorPalette = 'default' | 'ocean' | 'forest' | 'sunset';

type ThemeContextValue = {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  palette: ColorPalette;
  setMode: (mode: ThemeMode) => void;
  setPalette: (palette: ColorPalette) => void;
};

const defaultThemeContext: ThemeContextValue = {
  mode: 'system',
  resolvedMode: 'light',
  palette: 'default',
  setMode: () => {},
  setPalette: () => {},
};

const ThemeContext = createContext<ThemeContextValue>(defaultThemeContext);

const MODE_KEY = 'ui.theme.mode';
const PALETTE_KEY = 'ui.theme.palette';

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>('light');
  const [palette, setPalette] = useState<ColorPalette>(() => {
    const saved = localStorage.getItem(PALETTE_KEY);
    if (saved === 'default' || saved === 'ocean' || saved === 'forest' || saved === 'sunset') return saved;
    return 'default';
  });
  const resolvedMode: 'light' = 'light';

  useEffect(() => {
    if (mode !== 'light') {
      setMode('light');
      return;
    }
    localStorage.setItem(MODE_KEY, 'light');
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(PALETTE_KEY, palette);
  }, [palette]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme-mode', resolvedMode);
    root.setAttribute('data-theme-preference', mode);
    root.setAttribute('data-palette', palette);
    root.setAttribute('data-bs-theme', resolvedMode);
  }, [resolvedMode, mode, palette]);

  const value = useMemo(
    () => ({ mode, resolvedMode, palette, setMode, setPalette }),
    [mode, resolvedMode, palette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  return useContext(ThemeContext);
};
